import * as React from 'react';
import mergeRefs from 'react-merge-refs';
import useFocusVisible from '../utils/use_focus_visible';
import AccordionContext from './accordion_context';
import AnimatedHeight from '../shared/animated_height';
import IconChevronDown from '../icons/icon_chevron_down';
import { useItemStyles } from './use_styles';
import { jsx as ___EmotionJSX } from "@emotion/core";
var AccordionItem = /*#__PURE__*/React.forwardRef((_ref, ref) => {
  var {
    hasSingleBehavior,
    index,
    item,
    onSingleExpandChange
  } = _ref;
  var accordionContext = React.useContext(AccordionContext);
  var [focusedIndex, setFocusedIndex] = React.useState(null);
  var {
    content,
    disabled = false,
    header,
    id: itemId
  } = item;
  var {
    appearance,
    gutter,
    items,
    onCollapse,
    onExpand,
    singleExpandedItem
  } = accordionContext;
  var {
    handleBlurVisible,
    handleFocusVisible,
    ref: focusVisibleRef
  } = useFocusVisible();
  var accordionItemRef = React.useRef();
  var isExpanded = hasSingleBehavior && index === singleExpandedItem || !hasSingleBehavior && item && item.expanded === true;
  var handleClick = React.useCallback(event => {
    if (accordionItemRef.current !== event.target || disabled) return;
    event.preventDefault();
    if (isExpanded) {
      onCollapse === null || onCollapse === void 0 ? void 0 : onCollapse(event, index, item);
      if (hasSingleBehavior && index === singleExpandedItem) onSingleExpandChange(undefined);
    } else {
      onExpand === null || onExpand === void 0 ? void 0 : onExpand(event, index, item);
      if (hasSingleBehavior) onSingleExpandChange(index);
    }
  }, [disabled, hasSingleBehavior, index, isExpanded, item, onCollapse, onExpand, onSingleExpandChange, singleExpandedItem]);
  var handleBlur = React.useCallback(() => {
    setFocusedIndex(null);
    handleBlurVisible();
  }, [handleBlurVisible]);
  var handleFocus = React.useCallback(event => {
    if (accordionItemRef.current !== event.target || disabled) return;
    if (handleFocusVisible(event)) setFocusedIndex(index);
  }, [disabled, handleFocusVisible, index]);
  var handleKeyDown = React.useCallback(event => {
    if (accordionItemRef.current !== event.target || disabled) return;
    if (event.key === 'Enter' || event.key === ' ') {
      handleClick(event);
    }
  }, [disabled, handleClick]);
  var handleRef = mergeRefs([ref, accordionItemRef, focusVisibleRef]);
  var {
    length
  } = items;
  var {
    'aria-label': headerAriaLabel,
    id: headerId
  } = header || {};
  var isFocused = index === focusedIndex;
  var isLast = index === length - 1;
  var isFirst = index === 0;
  var style = useItemStyles({
    appearance,
    disabled,
    gutter,
    isExpanded,
    isFirst,
    isFocused,
    isLast
  });
  return ___EmotionJSX("div", {
    ref: ref,
    css: style.item
  }, ___EmotionJSX("div", {
    "aria-controls": itemId,
    "aria-expanded": isExpanded,
    "aria-label": headerAriaLabel,
    css: style.header,
    id: headerId,
    onBlur: handleBlur,
    onClick: handleClick,
    onFocus: handleFocus,
    onKeyDown: handleKeyDown,
    ref: handleRef,
    role: "button",
    tabIndex: 0
  }, header && ___EmotionJSX(React.Fragment, null, header.startArea && ___EmotionJSX("div", {
    css: style.headerItem
  }, header.startArea), header.centralArea && ___EmotionJSX("div", {
    className: "label",
    css: style.title
  }, header.centralArea), header.endArea && ___EmotionJSX("div", {
    css: style.headerItem
  }, header.endArea), ___EmotionJSX(IconChevronDown, {
    customStyle: style.chevron
  }))), header && header.previewArea && ___EmotionJSX("div", {
    css: style.previewArea
  }, header.previewArea), ___EmotionJSX(AnimatedHeight, {
    in: isExpanded
  }, ___EmotionJSX("div", {
    "aria-hidden": !isExpanded,
    "aria-labelledby": headerId,
    css: style.content,
    id: itemId
  }, content)));
});
AccordionItem.displayName = 'AccordionItem';
export default AccordionItem;